import { IconButton } from "@dropbox/dig-components/buttons";
import { Skeleton } from "@dropbox/dig-components/skeleton";
import { Text } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import {
  ChevronDownLine,
  ChevronUpLine,
} from "@dropbox/dig-icons/dist/mjs/assets";
import { MOTION_SETTINGS } from "@mirage/mosaics/Layout/constants";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { StackMoreSourcesCard } from "./StackMoreSourcesCard";
import { StackSourceCard } from "./StackSourceCard";
import type { StackConversationSource } from "./types";

import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import type { DashSourceType } from "@mirage/analytics/events/enums/dash_source_type";
import { PAP_Expand_StackSource } from "@mirage/analytics/events/types/expand_stack_source";
import { PAP_Expand_StackSourceRow } from "@mirage/analytics/events/types/expand_stack_source_row";
import { PAP_Hide_StackSourceRow } from "@mirage/analytics/events/types/hide_stack_source_row";
import styles from "./StackSourcesGroup.module.css";

type StackSourcesGroupProps = {
  defaultOpen?: boolean;
  hideTopBorder?: boolean;
  isLoading?: boolean;
  onSourceClick: (sourceType: DashSourceType, documentUuid?: string) => void;
  sources: StackConversationSource[];
  sourceType: DashSourceType;
  title: React.ReactNode;
};

const DEFAULT_NUM_SOURCES = 4;

export function StackSourcesGroup({
  defaultOpen = false,
  hideTopBorder = false,
  isLoading = false,
  onSourceClick,
  sources,
  sourceType,
  title,
}: StackSourcesGroupProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const { reportPapEvent } = useMirageAnalyticsContext();

  const sourcesToShow =
    !isExpanded && sources.length > DEFAULT_NUM_SOURCES
      ? sources.slice(0, DEFAULT_NUM_SOURCES - 1)
      : sources;

  const handleCollapseClick = () => {
    if (isOpen) {
      reportPapEvent(
        PAP_Hide_StackSourceRow({
          actionSurfaceComponent: "sidebar",
          dashActionSurface: "conversation_page",
          dashSourceType: sourceType,
          featureLine: "stacks",
        }),
      );
    } else {
      reportPapEvent(
        PAP_Expand_StackSourceRow({
          actionSurfaceComponent: "sidebar",
          dashActionSurface: "conversation_page",
          dashSourceType: sourceType,
          featureLine: "stacks",
        }),
      );
    }

    setIsOpen(!isOpen);
  };

  const handleExpandClick = () => {
    reportPapEvent(
      PAP_Expand_StackSource({
        actionSurfaceComponent: "sidebar",
        dashActionSurface: "conversation_page",
        dashSourceType: sourceType,
        featureLine: "stacks",
      }),
    );

    setIsExpanded(true);
  };

  return (
    <Box display="flex" flexDirection="column">
      <button
        className={styles.stackSourcesGroupHeaderButton}
        disabled={isLoading}
        onClick={handleCollapseClick}
      >
        <Box
          borderTop={hideTopBorder ? undefined : "Solid"}
          className={styles.stackSourcesGroupHeader}
        >
          {isLoading ? (
            <Skeleton.Text size="medium" width={140} />
          ) : (
            <Text variant="label" size="medium" color="subtle">
              {title}
            </Text>
          )}

          <Box className={styles.stackSourcesGroupHeaderIcon}>
            <IconButton disabled={isLoading} size="small" variant="borderless">
              <UIIcon src={isOpen ? ChevronUpLine : ChevronDownLine} />
            </IconButton>
          </Box>
        </Box>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            {...MOTION_SETTINGS}
            className={styles.stackSourcesCardContainer}
          >
            <Box className={styles.stackSourcesCard}>
              {isLoading && (
                <>
                  <Skeleton.Box height={92} />
                  <Skeleton.Box height={92} />
                  <Skeleton.Box height={92} />
                  <Skeleton.Box height={92} />
                </>
              )}
              {!isLoading &&
                sourcesToShow.map((source) => (
                  <StackSourceCard
                    key={source.uuid}
                    onSourceClick={onSourceClick}
                    source={source}
                    sourceType={sourceType}
                  />
                ))}
              {!isLoading && sourcesToShow.length < sources.length && (
                <StackMoreSourcesCard
                  onClick={handleExpandClick}
                  sources={sources.slice(sourcesToShow.length)}
                />
              )}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
}
